import Btn from 'components/buttons/Btn'
import Reloader from 'components/buttons/Reloader'
import CollapsibleListGroup from 'components/CollapsibleList/CollapsibleListGroup'
import EmptyMessage from 'components/EmptyMessage'
import { FixedWarning } from 'components/FixedWarnings/FixedWarning'
import I from 'components/I'
import Screen from 'components/layout/Screen'
import Tip from 'components/Tip'
import { useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import CardColumns from 'react-bootstrap/CardColumns'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useAccount } from 'state/Account'
import API from 'state/API'
import { useUser } from 'state/Auth'
import { editCategory, fetchCategories } from 'state/Categories'
import { PRIVILEGES } from 'state/Constants'
import { fetchItems, useGroupedItemsByCategory } from 'state/Items'
import { useRestaurants } from 'state/Restaurants'
import { klass, platform } from 'utils'
import ItemRow from './ItemRow'
import './styles.scss'

export default function ItemsPage() {
	const { t }      = useTranslation()
	const dispatch   = useDispatch()
	const groups     = useGroupedItemsByCategory()
	const account    = useAccount()
	const totalRest  = useRestaurants().length
	const LOCATIONS  = account.location_wording_plural
	const clickOrTap = platform.clickOrTap(t)
	const notAllowed = useUser().useFullPagePrivilege(PRIVILEGES().MENU_UPDATE, 'Menu Items')

	//this state is passed down to CollapsibleListGroup, where it only changes the open/close state when
	// forceClose is true. It goes causes an effect when the button is clicked (and changes to true), but after
	// a sec it's moved back (causing no harm) so the button can be triggered once again
	const [forceClose, toggleClose] = useState(false)

	if (notAllowed) {
		return notAllowed
	}

	function doForceClose() {
		toggleClose(true)
		window.setTimeout(() => toggleClose(false), 1000)
	}

	function doReload() {
		dispatch(fetchItems)
		dispatch(fetchCategories)
	}

	function doChangeStatus(catId, currentStatus) {
		dispatch(editCategory(catId, { is_shown: !currentStatus }))
	}

	const corner = [
		<Btn key="toggle" icon="compress-arrows-alt" title={t("Collapse all categories")} onClick={doForceClose}><span className="d-none d-xs-inline">{t('Collapse')}</span></Btn>,
		<Reloader key="reloader" size="sm" resource={API.RESOURCES.ITEMS} reloader={doReload}/>
	]
	return <Screen title={t('Menu items')} corner={corner}>
		<FixedWarning variant="warning" icon="map-marker-exclamation" show={totalRest > 1}>{t('Applies to all {{LOCATIONS}}.', { LOCATIONS })}</FixedWarning>

		<Screen.Content>
			{groups.length?
				<CardColumns>
					{groups.map(({ category: cat, items }) => {
						const header = cat?
							<><span dangerouslySetInnerHTML={{__html: cat.display_name }}/> {cat.tag && <Badge variant="secondary">{cat.tag}</Badge>}</> :
							<I force>{t('No category')}</I>

						const corner = cat?
							<CollapsibleListGroup.Corner
								action={() => doChangeStatus(cat.id, cat.is_shown)}
								text={cat.is_shown? t('Shown; {{clickOrTap}} to hide', { clickOrTap }) : t('Hidden; {{clickOrTap}} to show', { clickOrTap })}
								icon={cat.is_shown? 'eye' : 'eye-slash'}
								variant={cat.is_shown? 'success' : 'danger'}
								className={cat.is_shown? 'shown' : 'hidden'}
							/>
						:
							<I force className="small">
								{t('None shown')}
								<Tip.Bubble text={items.length == 1? t("This item won't show for customers since it has no category to be listed at.") : t("Those items won't show for customers since they have no category to be listed at.")}/> {/* This seems to need pluralization in Chinese, but since Chinese usually doesn't do that, we can't pluralize using i18next */}
							</I>
						return (
							<CollapsibleListGroup key={cat?.id || 0} openByDefault split {...{ header, forceClose, corner, ...klass({ noCategory: !cat }) }}>
								{items.map(item => <ItemRow item={item} key={item.id}/>)}
							</CollapsibleListGroup>
						)
					})}
				</CardColumns>
			:
				<EmptyMessage icon="book-open" resource={[API.RESOURCES.ITEMS, API.RESOURCES.CATEGORIES]} msg={<>
					{t('We couldn\'t find Menu Items...')}<br/>{t('CONTACT_ERROR')}
				</>}/>
			}
		</Screen.Content>
	</Screen>
}
